import { useState } from "react";
import styled from "styled-components";

import { useIsPhoneLayout } from "@pages/AdminDashboard/theme/AdminLayout";
import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { Button } from "@pages/AdminDashboard/ui/Button";
import { ConfirmDialog, ConfirmDialogProps } from "@pages/AdminDashboard/ui/ConfirmDialog";
import { UiKitSection } from "@pages/AdminDashboard/ui/UiKitSection";

type ExampleName = "Destructive" | "Non-destructive" | "With custom body" | "Pending";

const replaceConsequences = [
  "OID b72e-test-0002 is cancelled and stays on the Case as history.",
  "A new Order is created under the same invoice and sent to the pharmacy.",
];

const replaceDialog = {
  eyebrow: "Replace Order",
  title: "Replace OID b72e-test-0002?",
  consequences: replaceConsequences,
  note: "The User keeps Case ID #10482 and is not charged again.",
  dismissLabel: "Keep current Order",
  confirmLabel: "Replace Order",
  pendingLabel: "Replacing…",
};

const prescriptions = [
  { name: "Acetazolamide 125 mg", detail: "20 tablets · 10 days" },
  { name: "Azithromycin 500 mg", detail: "3 tablets · 3 days" },
];

const [spaceXs, spaceSm, spaceMd, , spaceXl] = adminTokens.spacing;
const { color, radius, typeScale } = adminTokens;
const contentAreaMinHeight = 640;
const navigationWidth = 160;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(300px, 100%), 1fr));
  gap: ${spaceMd}px;
  margin-bottom: ${spaceXl}px;
`;

const Example = styled.div.attrs({ role: "group" })`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spaceXs}px;
  border: 1px solid ${color.border};
  border-radius: ${radius.row}px;
  padding: ${spaceMd}px;
`;

const Caption = styled.span`
  color: ${color.inkSoft};
  font-family: ${typeScale.meta.fontFamily};
  font-size: ${typeScale.meta.fontSize};
`;

const Stage = styled.div`
  display: flex;
  gap: ${spaceMd}px;
`;

const Navigation = styled.nav`
  flex: none;
  box-sizing: border-box;
  width: ${navigationWidth}px;
  padding: ${spaceMd}px;
  border: 1px solid ${color.border};
  border-radius: ${radius.row}px;
  background-color: ${color.surface};
  color: ${color.navy};
  font-family: ${typeScale.body.fontFamily};
  font-size: ${typeScale.body.fontSize};
`;

const ContentArea = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  min-height: ${contentAreaMinHeight}px;
  box-sizing: border-box;
  padding: ${spaceMd}px;
  border: 1px solid ${color.border};
  border-radius: ${radius.row}px;
  background-color: ${color.canvas};
  color: ${color.navy};
  font-family: ${typeScale.body.fontFamily};
  font-size: ${typeScale.body.fontSize};
`;

const PrescriptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spaceSm}px;
  color: ${color.navy};
  font-family: ${typeScale.body.fontFamily};
  font-size: ${typeScale.body.fontSize};
`;

const PrescriptionRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${spaceMd}px;
  padding: ${spaceSm}px ${spaceMd}px;
  border: 1px solid ${color.periwinkle};
  border-radius: ${radius.row}px;
  background-color: ${color.canvas};
`;

const PrescriptionPicker = () => (
  <PrescriptionList>
    <span>Prescriptions to send</span>
    {prescriptions.map(({ name, detail }) => (
      <PrescriptionRow key={name}>
        <span>{name}</span>
        <span>{detail}</span>
      </PrescriptionRow>
    ))}
  </PrescriptionList>
);

type DialogContent = Pick<
  ConfirmDialogProps,
  | "eyebrow"
  | "title"
  | "consequences"
  | "note"
  | "dismissLabel"
  | "confirmLabel"
  | "pendingLabel"
  | "isDestructive"
  | "isPending"
>;

const contentFor = (example: ExampleName): DialogContent => {
  if (example === "Destructive") {
    return {
      eyebrow: "Cancel Order",
      title: "Cancel OID b72e-test-0002?",
      consequences: [
        "It is in progress at the pharmacy, so we ask the pharmacy to stop it.",
        "It is marked cancelled here and stays on the Case as history.",
      ],
      note: "This can’t be undone. If the User still needs their medication, use Replace Order instead.",
      dismissLabel: "Keep Order",
      confirmLabel: "Cancel Order",
      pendingLabel: "Cancelling…",
      isDestructive: true,
    };
  }

  return { ...replaceDialog, isPending: example === "Pending" };
};

const examples: ExampleName[] = ["Destructive", "Non-destructive", "With custom body", "Pending"];

// On a phone there is no navigation beside the content, so the example opens over
// the whole window, as a screen's dialog does there (PHONE-R8).
export const ConfirmDialogSection = () => {
  const isPhone = useIsPhoneLayout();
  const [openExample, setOpenExample] = useState<ExampleName | null>(null);
  const [contentArea, setContentArea] = useState<HTMLDivElement | null>(null);

  const close = () => setOpenExample(null);

  const dialog = openExample && (
    <ConfirmDialog
      isOpen
      container={isPhone ? undefined : contentArea}
      {...contentFor(openExample)}
      onConfirm={close}
      onDismiss={close}
    >
      {openExample === "With custom body" && <PrescriptionPicker />}
      {openExample === "Pending" && (
        <Button look="secondary" size="dialog" onClick={close}>
          Finish request (gallery only)
        </Button>
      )}
    </ConfirmDialog>
  );

  return (
    <UiKitSection title="Confirm dialog">
      <Grid>
        {examples.map((example) => (
          <Example key={example} aria-label={example}>
            <Caption>{example}</Caption>
            <Button look="secondary" onClick={() => setOpenExample(example)}>
              {`Open ${example.toLowerCase()} dialog`}
            </Button>
          </Example>
        ))}
      </Grid>
      {isPhone ? (
        dialog
      ) : (
        <Stage>
          <Navigation aria-label="Example navigation">Navigation stays visible</Navigation>
          <ContentArea ref={setContentArea}>
            Content area: the scrim covers only this area.
            {dialog}
          </ContentArea>
        </Stage>
      )}
    </UiKitSection>
  );
};
